import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DndContext, closestCorners, PointerSensor, KeyboardSensor,
  useSensor, useSensors, useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, Trash2, CheckSquare, Square, Calendar, Flag, Tag,
  ChevronDown, ChevronRight, List as ListIcon, Columns, GripVertical,
  Pencil, X, Link2, Wand2, GitBranch, Cloud, CloudRain, CloudSun,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import TreeView from '../components/tasks/TreeView';
import BreakdownDialog from '../components/tasks/BreakdownDialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Config ────────────────────────────────────────────────────────

const STATUSES = [
  { key: 'backlog',     label: 'Backlog',      color: '#6B7A73' },
  { key: 'today',       label: 'Today',        color: '#2D6B68' },
  { key: 'in_progress', label: 'In Progress',  color: '#C7892A' },
  { key: 'blocked',     label: 'Blocked',      color: '#B84A3E' },
  { key: 'done',        label: 'Done',         color: '#4C8B4F' },
];

const PRIORITY_CFG = {
  low:    { label: 'Low',    color: 'text-slate-500  bg-slate-50 border-slate-200'  },
  medium: { label: 'Medium', color: 'text-amber-700  bg-amber-50 border-amber-200'  },
  high:   { label: 'High',   color: 'text-red-700    bg-red-50   border-red-200'    },
};

const CATEGORY_CFG = {
  work:     { label: 'Work',     color: 'text-[#2D6B68] bg-[#E5F0EE] border-[#C8D4CE]' },
  home:     { label: 'Home',     color: 'text-blue-700  bg-blue-50  border-blue-200'   },
  personal: { label: 'Personal', color: 'text-purple-700 bg-purple-50 border-purple-200' },
};

const STATUS_KEYS = STATUSES.map(s => s.key);

// ─── Utilities ─────────────────────────────────────────────────────

const fmtDueDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const isOverdue = (iso) => {
  if (!iso) return false;
  const due = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return due < today;
};

// ─── Main ──────────────────────────────────────────────────────────

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');          // list | kanban
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterEstimate, setFilterEstimate] = useState('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [breakdownTask, setBreakdownTask] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  // ── Fetch ────────────────────────────────────────────────────
  useEffect(() => { fetchTasks(); fetchEstimates(); }, []);

  const fetchTasks = async () => {
    try {
      const res = await fetch(`${API}/tasks`, { credentials: 'include' });
      if (res.ok) setTasks(await res.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const fetchEstimates = async () => {
    try {
      const res = await fetch(`${API}/estimates`, { credentials: 'include' });
      if (res.ok) setEstimates(await res.json());
    } catch { /* ignore */ }
  };

  // ── Helpers ──────────────────────────────────────────────────
  const estimateById = useMemo(() => {
    const m = new Map();
    estimates.forEach(e => m.set(e.estimate_id, e));
    return m;
  }, [estimates]);

  const filtered = useMemo(() => {
    return tasks.filter(t => {
      if (filterCategory !== 'all' && t.category !== filterCategory) return false;
      if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
      if (filterEstimate === '__none__') {
        if (t.estimate_id) return false;
      } else if (filterEstimate !== 'all' && t.estimate_id !== filterEstimate) {
        return false;
      }
      return true;
    });
  }, [tasks, filterCategory, filterPriority, filterEstimate]);

  // Parent/child buckets
  const { parents, childrenByParent } = useMemo(() => {
    const parents = filtered.filter(t => !t.parent_task_id);
    const childrenByParent = {};
    filtered.forEach(t => {
      if (t.parent_task_id) {
        (childrenByParent[t.parent_task_id] ||= []).push(t);
      }
    });
    return { parents, childrenByParent };
  }, [filtered]);

  // Grouped by status for kanban
  const byStatus = useMemo(() => {
    const grp = Object.fromEntries(STATUS_KEYS.map(k => [k, []]));
    parents.forEach(t => {
      if (grp[t.status]) grp[t.status].push(t);
    });
    // Sort within each lane by position
    Object.values(grp).forEach(arr => arr.sort((a, b) => (a.position || 0) - (b.position || 0)));
    return grp;
  }, [parents]);

  // ── Task mutations ───────────────────────────────────────────
  const upsertTask = useCallback(async (patch, existingId = null) => {
    if (existingId) {
      const res = await fetch(`${API}/tasks/${existingId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(patch),
      });
      if (res.ok) {
        const updated = await res.json();
        setTasks(prev => prev.map(t => t.task_id === updated.task_id ? updated : t));
        return updated;
      }
      toast.error('Failed to update task');
    } else {
      const res = await fetch(`${API}/tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(patch),
      });
      if (res.ok) {
        const created = await res.json();
        setTasks(prev => [...prev, created]);
        return created;
      }
      toast.error('Failed to create task');
    }
  }, []);

  const deleteTask = async (taskId) => {
    const res = await fetch(`${API}/tasks/${taskId}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) {
      // Cascade remove in UI (subtasks too)
      setTasks(prev => prev.filter(t => t.task_id !== taskId && t.parent_task_id !== taskId));
      toast.success('Task deleted');
    }
  };

  const toggleDone = async (task) => {
    const nextStatus = task.status === 'done' ? 'today' : 'done';
    await upsertTask({ status: nextStatus }, task.task_id);
  };

  // Weather check — hits the backend, stores result on the task and
  // mirrors it into local state so the card badge re-renders.
  const [weatherLoadingIds, setWeatherLoadingIds] = useState(new Set());
  const checkWeather = async (task) => {
    if (!task?.task_id) return;
    if (!task.due_date) {
      toast.error('Set a due date first to check the weather');
      return;
    }
    setWeatherLoadingIds(prev => new Set(prev).add(task.task_id));
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}/weather-check`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Weather check failed');
        return;
      }
      setTasks(prev => prev.map(t =>
        t.task_id === task.task_id ? { ...t, weather_check: data } : t
      ));
      const riskLabel = { green: 'Looks clear', amber: 'Mixed — review', red: 'High risk' }[data.risk_level] || 'Checked';
      toast.success(`${riskLabel} — ${data.summary}, ${Math.round(data.temp_low)}°/${Math.round(data.temp_high)}°C`);
    } catch {
      toast.error('Weather check failed');
    } finally {
      setWeatherLoadingIds(prev => {
        const n = new Set(prev);
        n.delete(task.task_id);
        return n;
      });
    }
  };

  const openEditor = (task = null, parentId = null) => {
    setEditingTask(task || {
      title: '', description: '',
      status: 'today', priority: 'medium', category: 'work',
      due_date: '', estimate_id: null, parent_task_id: parentId,
    });
    setEditorOpen(true);
  };

  const openBreakdown = (task) => {
    setBreakdownTask(task);
    setBreakdownOpen(true);
  };

  const handleBreakdownApply = async (subtasks) => {
    if (!breakdownTask) return;
    const res = await fetch(`${API}/tasks/${breakdownTask.task_id}/break-down/apply`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ subtasks }),
    });
    if (res.ok) {
      const { created } = await res.json();
      setTasks(prev => [...prev, ...(created || [])]);
      setExpandedParents(prev => new Set(prev).add(breakdownTask.task_id));
      toast.success(`Added ${(created || []).length} subtask${(created || []).length !== 1 ? 's' : ''}`);
      setBreakdownOpen(false);
    } else {
      toast.error('Failed to create subtasks');
    }
  };

  const toggleParentExpand = (id) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Drag-and-drop (kanban + list-within-status) ──────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeTask = tasks.find(t => t.task_id === active.id);
    if (!activeTask) return;

    // Dropped on a column (empty lane or above all tasks)
    const overId = String(over.id);
    let targetStatus = activeTask.status;
    let targetTasks;

    if (overId.startsWith('column-')) {
      targetStatus = overId.replace('column-', '');
      targetTasks = byStatus[targetStatus] || [];
      // Append to end
      const maxPos = targetTasks.length ? Math.max(...targetTasks.map(t => t.position || 0)) : 0;
      await upsertTask(
        { status: targetStatus, position: maxPos + 10 },
        activeTask.task_id
      );
      return;
    }

    const overTask = tasks.find(t => t.task_id === overId);
    if (!overTask) return;

    targetStatus = overTask.status;
    targetTasks = byStatus[targetStatus] || [];
    const activeIdx = targetTasks.findIndex(t => t.task_id === active.id);
    const overIdx = targetTasks.findIndex(t => t.task_id === over.id);
    let reordered;
    if (activeIdx >= 0) {
      // Same lane — simple reorder
      reordered = arrayMove(targetTasks, activeIdx, overIdx);
    } else {
      // Cross-lane — insert above the over-task
      reordered = [...targetTasks];
      reordered.splice(overIdx, 0, { ...activeTask, status: targetStatus });
    }
    // Reassign positions in increments of 10
    const updates = reordered.map((t, i) => ({
      task_id: t.task_id,
      status: targetStatus,
      position: (i + 1) * 10,
    }));
    // Optimistic local update
    setTasks(prev => {
      const map = new Map(prev.map(t => [t.task_id, t]));
      updates.forEach(u => {
        const t = map.get(u.task_id);
        if (t) map.set(u.task_id, { ...t, status: u.status, position: u.position });
      });
      return Array.from(map.values());
    });
    // Persist
    fetch(`${API}/tasks/reorder`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ updates }),
    }).catch(() => toast.error('Reorder did not save'));
  };

  // ── Render ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="animate-fade-in flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const openTaskCount = parents.filter(t => t.status !== 'done').length;

  return (
    <div className="animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Tasks
          </h1>
          <p className="text-sm text-[#6B7A73] mt-1">
            {openTaskCount} open · {parents.length} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center bg-[#F0F3F1] rounded-md p-0.5" data-testid="view-toggle">
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1.5 ${
                view === 'list' ? 'bg-white text-[#1A1D1C] shadow-sm' : 'text-[#6B7A73]'
              }`}
              data-testid="view-list"
            >
              <ListIcon className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setView('kanban')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1.5 ${
                view === 'kanban' ? 'bg-white text-[#1A1D1C] shadow-sm' : 'text-[#6B7A73]'
              }`}
              data-testid="view-kanban"
            >
              <Columns className="w-3.5 h-3.5" /> Board
            </button>
            <button
              onClick={() => setView('tree')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1.5 ${
                view === 'tree' ? 'bg-white text-[#1A1D1C] shadow-sm' : 'text-[#6B7A73]'
              }`}
              data-testid="view-tree"
            >
              <GitBranch className="w-3.5 h-3.5" /> Tree
            </button>
          </div>
          <Button
            onClick={() => openEditor(null)}
            className="bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-5 py-2 font-medium"
            data-testid="new-task-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Task
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-36 bg-white border-[#E6EBE8] text-sm" data-testid="filter-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_CFG).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-36 bg-white border-[#E6EBE8] text-sm" data-testid="filter-priority">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {Object.entries(PRIORITY_CFG).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterEstimate} onValueChange={setFilterEstimate}>
          <SelectTrigger className="w-56 bg-white border-[#E6EBE8] text-sm" data-testid="filter-estimate">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Estimates</SelectItem>
            <SelectItem value="__none__">No estimate linked</SelectItem>
            {estimates.map(e => (
              <SelectItem key={e.estimate_id} value={e.estimate_id}>
                {e.estimate_number} — {(e.title || '').slice(0, 30)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Views */}
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        {view === 'kanban' ? (
          <KanbanView
            byStatus={byStatus}
            childrenByParent={childrenByParent}
            expandedParents={expandedParents}
            toggleParentExpand={toggleParentExpand}
            estimateById={estimateById}
            onEdit={openEditor}
            onDelete={deleteTask}
            onToggleDone={toggleDone}
            onAddSubtask={(parentId) => openEditor(null, parentId)}
            onBreakdown={openBreakdown}
            onCheckWeather={checkWeather}
            weatherLoadingIds={weatherLoadingIds}
          />
        ) : view === 'tree' ? (
          <TreeView
            parents={parents}
            childrenByParent={childrenByParent}
            estimateById={estimateById}
            onEdit={openEditor}
            onDelete={deleteTask}
            onToggleDone={toggleDone}
            onAddSubtask={(parentId) => openEditor(null, parentId)}
            onBreakdown={openBreakdown}
          />
        ) : (
          <ListView
            parents={parents}
            childrenByParent={childrenByParent}
            expandedParents={expandedParents}
            toggleParentExpand={toggleParentExpand}
            estimateById={estimateById}
            onEdit={openEditor}
            onDelete={deleteTask}
            onToggleDone={toggleDone}
            onAddSubtask={(parentId) => openEditor(null, parentId)}
            onBreakdown={openBreakdown}
            onCheckWeather={checkWeather}
            weatherLoadingIds={weatherLoadingIds}
          />
        )}
      </DndContext>

      {/* Editor */}
      <TaskEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        task={editingTask}
        estimates={estimates}
        onSave={async (patch) => {
          const id = editingTask && editingTask.task_id;
          await upsertTask(patch, id || null);
          setEditorOpen(false);
        }}
      />

      {/* AI Break Down */}
      <BreakdownDialog
        open={breakdownOpen}
        onOpenChange={setBreakdownOpen}
        task={breakdownTask}
        onApply={handleBreakdownApply}
      />
    </div>
  );
}

// ─── Kanban view ───────────────────────────────────────────────────

function KanbanView({
  byStatus, childrenByParent, expandedParents, toggleParentExpand,
  estimateById, onEdit, onDelete, onToggleDone, onAddSubtask, onBreakdown,
  onCheckWeather, weatherLoadingIds,
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kanban-board">
      {STATUSES.map(s => (
        <KanbanColumn
          key={s.key}
          status={s}
          tasks={byStatus[s.key] || []}
          childrenByParent={childrenByParent}
          expandedParents={expandedParents}
          toggleParentExpand={toggleParentExpand}
          estimateById={estimateById}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleDone={onToggleDone}
          onAddSubtask={onAddSubtask}
          onBreakdown={onBreakdown}
          onCheckWeather={onCheckWeather}
          weatherLoadingIds={weatherLoadingIds}
        />
      ))}
    </div>
  );
}

function KanbanColumn({
  status, tasks, childrenByParent, expandedParents, toggleParentExpand,
  estimateById, onEdit, onDelete, onToggleDone, onAddSubtask, onBreakdown,
  onCheckWeather, weatherLoadingIds,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `column-${status.key}` });
  const ids = tasks.map(t => t.task_id);
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col bg-[#F9FAFA] border border-[#E6EBE8] rounded-md min-h-[300px] ${
        isOver ? 'ring-2 ring-[#2D6B68]/30' : ''
      }`}
      data-testid={`column-${status.key}`}
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#E6EBE8]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: status.color }} />
          <h3 className="text-xs uppercase tracking-wider font-semibold text-[#3A423E] truncate">
            {status.label}
          </h3>
        </div>
        <span className="text-[10px] text-[#6B7A73] tabular-nums font-mono" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          {tasks.length}
        </span>
      </div>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="flex-1 p-2 space-y-2">
          {tasks.map(t => (
            <TaskCard
              key={t.task_id}
              task={t}
              children={childrenByParent[t.task_id] || []}
              expanded={expandedParents.has(t.task_id)}
              onExpand={() => toggleParentExpand(t.task_id)}
              estimate={t.estimate_id ? estimateById.get(t.estimate_id) : null}
              onEdit={() => onEdit(t)}
              onDelete={() => onDelete(t.task_id)}
              onToggleDone={() => onToggleDone(t)}
              onAddSubtask={() => onAddSubtask(t.task_id)}
              onBreakdown={() => onBreakdown(t)}
              onCheckWeather={onCheckWeather ? () => onCheckWeather(t) : null}
              weatherLoading={weatherLoadingIds?.has(t.task_id)}
              onEditChild={onEdit}
              onDeleteChild={onDelete}
              onToggleDoneChild={onToggleDone}
            />
          ))}
          {tasks.length === 0 && (
            <p className="text-[11px] text-[#6B7A73] italic text-center py-4">Drop tasks here</p>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

// ─── List view ─────────────────────────────────────────────────────

function ListView({
  parents, childrenByParent, expandedParents, toggleParentExpand,
  estimateById, onEdit, onDelete, onToggleDone, onAddSubtask, onBreakdown,
  onCheckWeather, weatherLoadingIds,
}) {
  // Group by status, shown top → bottom in canonical order
  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUS_KEYS.map(k => [k, []]));
    parents.forEach(t => { (g[t.status] || g.backlog).push(t); });
    Object.values(g).forEach(arr => arr.sort((a, b) => (a.position || 0) - (b.position || 0)));
    return g;
  }, [parents]);

  return (
    <div className="space-y-6" data-testid="list-view">
      {STATUSES.map(s => {
        const items = grouped[s.key] || [];
        if (items.length === 0) return null;
        return (
          <div key={s.key}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
              <h3 className="text-xs uppercase tracking-wider font-semibold text-[#3A423E]">{s.label}</h3>
              <span className="text-[11px] text-[#6B7A73]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                {items.length}
              </span>
            </div>
            <SortableContext items={items.map(t => t.task_id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5 bg-white border border-[#E6EBE8] rounded-md p-2">
                {items.map(t => (
                  <TaskCard
                    key={t.task_id}
                    task={t}
                    compact
                    children={childrenByParent[t.task_id] || []}
                    expanded={expandedParents.has(t.task_id)}
                    onExpand={() => toggleParentExpand(t.task_id)}
                    estimate={t.estimate_id ? estimateById.get(t.estimate_id) : null}
                    onEdit={() => onEdit(t)}
                    onDelete={() => onDelete(t.task_id)}
                    onToggleDone={() => onToggleDone(t)}
                    onAddSubtask={() => onAddSubtask(t.task_id)}
                    onBreakdown={() => onBreakdown(t)}
                    onCheckWeather={onCheckWeather ? () => onCheckWeather(t) : null}
                    weatherLoading={weatherLoadingIds?.has(t.task_id)}
                    onEditChild={onEdit}
                    onDeleteChild={onDelete}
                    onToggleDoneChild={onToggleDone}
                  />
                ))}
              </div>
            </SortableContext>
          </div>
        );
      })}
    </div>
  );
}

// ─── Task card (shared) ────────────────────────────────────────────

const RISK_CFG = {
  green: { color: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CloudSun, label: 'Clear' },
  amber: { color: 'text-amber-700 bg-amber-50 border-amber-200', Icon: Cloud, label: 'Mixed' },
  red:   { color: 'text-red-700 bg-red-50 border-red-200', Icon: CloudRain, label: 'High risk' },
};

function WeatherBadge({ wc }) {
  if (!wc) return null;
  const cfg = RISK_CFG[wc.risk_level] || RISK_CFG.green;
  const Icon = cfg.Icon;
  const locLabel = wc.location?.label || 'Montréal';
  const fetchedAgo = wc.fetched_at
    ? new Date(wc.fetched_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
    : '';
  const tooltip = [
    `${wc.summary || '—'} · ${Math.round(wc.temp_low)}°/${Math.round(wc.temp_high)}°C`,
    `Rain ${wc.precip_pct ?? 0}%${wc.precip_mm ? ` (${wc.precip_mm}mm)` : ''}`,
    `Wind ${Math.round(wc.wind_kph || 0)} km/h`,
    `Location: ${locLabel}${wc.location?.source === 'default' ? ' (default — no address)' : ''}`,
    `Checked ${fetchedAgo}`,
  ].join(' · ');
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${cfg.color}`}
      title={tooltip}
    >
      <Icon className="w-2.5 h-2.5" />
      {cfg.label} · {wc.precip_pct ?? 0}% · {Math.round(wc.temp_low)}°/{Math.round(wc.temp_high)}°
    </span>
  );
}

function TaskCard({
  task, compact, children = [], expanded, onExpand, estimate,
  onEdit, onDelete, onToggleDone, onAddSubtask, onBreakdown,
  onCheckWeather, weatherLoading,
  onEditChild, onDeleteChild, onToggleDoneChild,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: task.task_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const done = task.status === 'done';
  const overdue = isOverdue(task.due_date);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-[#E6EBE8] rounded-md ${compact ? 'p-2' : 'p-2.5'} hover:border-[#C8D4CE] transition-colors`}
      data-testid={`task-${task.task_id}`}
    >
      <div className="flex items-start gap-2">
        <button
          {...attributes} {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-[#C8D4CE] hover:text-[#6B7A73] pt-0.5"
          data-testid={`drag-${task.task_id}`}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onToggleDone}
          className="flex-shrink-0 mt-0.5"
          data-testid={`toggle-${task.task_id}`}
        >
          {done
            ? <CheckSquare className="w-4 h-4 text-[#2D6B68]" />
            : <Square className="w-4 h-4 text-[#6B7A73] hover:text-[#2D6B68]" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <p
              className={`text-sm flex-1 ${done ? 'line-through text-[#6B7A73]' : 'text-[#1A1D1C]'} cursor-pointer`}
              onClick={onEdit}
            >
              {task.title}
            </p>
            {children.length > 0 && (
              <button
                onClick={onExpand}
                className="flex-shrink-0 text-[10px] text-[#6B7A73] hover:text-[#1A1D1C] flex items-center gap-0.5 mt-0.5"
                data-testid={`expand-${task.task_id}`}
              >
                {expanded
                  ? <ChevronDown className="w-3 h-3" />
                  : <ChevronRight className="w-3 h-3" />}
                {children.length}
              </button>
            )}
          </div>

          {/* Meta row */}
          <div className="flex items-center flex-wrap gap-1.5 mt-1">
            {task.priority && task.priority !== 'medium' && (
              <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY_CFG[task.priority]?.color || ''}`}>
                <Flag className="w-2.5 h-2.5" /> {PRIORITY_CFG[task.priority]?.label || task.priority}
              </span>
            )}
            {task.category && (
              <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${CATEGORY_CFG[task.category]?.color || ''}`}>
                <Tag className="w-2.5 h-2.5" /> {CATEGORY_CFG[task.category]?.label || task.category}
              </span>
            )}
            {task.due_date && (
              <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${
                overdue && !done
                  ? 'text-red-700 bg-red-50 border-red-200'
                  : 'text-[#3A423E] bg-[#F5F6F5] border-[#E6EBE8]'
              }`}>
                <Calendar className="w-2.5 h-2.5" /> {fmtDueDate(task.due_date)}
              </span>
            )}
            {estimate && (
              <span
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border text-[#C7892A] bg-amber-50 border-amber-200"
                title={estimate.title}
              >
                <Link2 className="w-2.5 h-2.5" /> {estimate.estimate_number}
              </span>
            )}
            {task.weather_check && <WeatherBadge wc={task.weather_check} />}
          </div>

          {/* Subtasks */}
          {expanded && children.length > 0 && (
            <div className="mt-2 pl-2 border-l-2 border-[#E6EBE8] space-y-1">
              {children.map(ch => (
                <SubtaskRow
                  key={ch.task_id}
                  task={ch}
                  onEdit={() => onEditChild(ch)}
                  onDelete={() => onDeleteChild(ch.task_id)}
                  onToggleDone={() => onToggleDoneChild(ch)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Row actions */}
        <div className="flex items-center gap-0.5">
          {onCheckWeather && task.due_date && (
            <button
              onClick={(e) => { e.stopPropagation(); onCheckWeather(); }}
              disabled={weatherLoading}
              className="p-1 text-[#6B7A73] hover:text-[#2D6B68] hover:bg-[#F0F3F1] rounded disabled:opacity-50"
              title="Check weather for due date"
              data-testid={`weather-${task.task_id}`}
            >
              {weatherLoading
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Cloud className="w-3 h-3" />}
            </button>
          )}
          {onBreakdown && (
            <button
              onClick={(e) => { e.stopPropagation(); onBreakdown(); }}
              className="p-1 text-[#6B7A73] hover:text-[#2D6B68] hover:bg-[#F0F3F1] rounded"
              title="AI break down"
              data-testid={`breakdown-${task.task_id}`}
            >
              <Wand2 className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={onAddSubtask}
            className="p-1 text-[#6B7A73] hover:text-[#2D6B68] hover:bg-[#F0F3F1] rounded"
            title="Add subtask"
            data-testid={`subtask-${task.task_id}`}
          >
            <Plus className="w-3 h-3" />
          </button>
          <button
            onClick={onEdit}
            className="p-1 text-[#6B7A73] hover:text-[#2D6B68] hover:bg-[#F0F3F1] rounded"
            title="Edit"
            data-testid={`edit-${task.task_id}`}
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 text-[#6B7A73] hover:text-red-600 hover:bg-red-50 rounded"
            title="Delete"
            data-testid={`delete-${task.task_id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function SubtaskRow({ task, onEdit, onDelete, onToggleDone }) {
  const done = task.status === 'done';
  return (
    <div className="flex items-center gap-1.5 group" data-testid={`subtask-row-${task.task_id}`}>
      <button onClick={onToggleDone}>
        {done
          ? <CheckSquare className="w-3.5 h-3.5 text-[#2D6B68]" />
          : <Square className="w-3.5 h-3.5 text-[#6B7A73] hover:text-[#2D6B68]" />}
      </button>
      <span
        className={`text-xs flex-1 cursor-pointer ${done ? 'line-through text-[#6B7A73]' : 'text-[#3A423E]'}`}
        onClick={onEdit}
      >
        {task.title}
      </span>
      <button
        onClick={onDelete}
        className="p-0.5 text-[#6B7A73] hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Task Editor Dialog ────────────────────────────────────────────

function TaskEditorDialog({ open, onOpenChange, task, estimates, onSave }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (task) setForm({ ...task });
  }, [task]);

  if (!form) return null;

  const isNew = !form.task_id;
  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const submit = async () => {
    if (!form.title?.trim()) {
      toast.error('Give this task a title');
      return;
    }
    const patch = {
      title: form.title.trim(),
      description: form.description || '',
      status: form.status || 'today',
      priority: form.priority || 'medium',
      category: form.category || 'work',
      due_date: form.due_date || null,
      estimate_id: form.estimate_id || null,
    };
    if (form.parent_task_id) patch.parent_task_id = form.parent_task_id;
    await onSave(patch);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>
            {isNew ? (form.parent_task_id ? 'Add subtask' : 'New task') : 'Edit task'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Title</label>
            <Input
              autoFocus
              value={form.title || ''}
              onChange={e => update({ title: e.target.value })}
              className="bg-white border-[#E6EBE8] mt-1"
              placeholder="e.g., Order quartz slab for kitchen"
              data-testid="task-title-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Description (optional)</label>
            <Textarea
              value={form.description || ''}
              onChange={e => update({ description: e.target.value })}
              rows={2}
              className="bg-white border-[#E6EBE8] mt-1 resize-none text-sm"
              placeholder="Notes, context, links…"
              data-testid="task-description-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Status</label>
              <Select value={form.status || 'today'} onValueChange={v => update({ status: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6EBE8] text-sm" data-testid="task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Priority</label>
              <Select value={form.priority || 'medium'} onValueChange={v => update({ priority: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6EBE8] text-sm" data-testid="task-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_CFG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Category</label>
              <Select value={form.category || 'work'} onValueChange={v => update({ category: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6EBE8] text-sm" data-testid="task-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_CFG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Due date</label>
              <Input
                type="date"
                value={form.due_date ? form.due_date.slice(0, 10) : ''}
                onChange={e => update({ due_date: e.target.value || null })}
                className="bg-white border-[#E6EBE8] mt-1 text-sm"
                data-testid="task-due-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Link to estimate</label>
              <Select
                value={form.estimate_id || '__none__'}
                onValueChange={v => update({ estimate_id: v === '__none__' ? null : v })}
              >
                <SelectTrigger className="mt-1 bg-white border-[#E6EBE8] text-sm" data-testid="task-estimate-select">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No estimate</SelectItem>
                  {estimates.map(e => (
                    <SelectItem key={e.estimate_id} value={e.estimate_id}>
                      {e.estimate_number} — {(e.title || '').slice(0, 30)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E6EBE8]">
            Cancel
          </Button>
          <Button onClick={submit} className="bg-[#2D6B68] text-white hover:bg-[#235552]" data-testid="task-save-btn">
            {isNew ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

